/**
 * DeltaBadge Component
 *
 * Displays a delta value with appropriate styling (e.g., "+2 quality" or "-3 flags")
 */

import React from 'react';
import {
  ArrowDownward as NegativeIcon,
  ArrowUpward as PositiveIcon,
  Remove as NoChangeIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { TREND_COLORS } from '../../constants';

interface DeltaBadgeProps {
  /** The delta value */
  delta: number | null | undefined;
  /** Label to show after the value (e.g., "quality", "flags") */
  label?: string;
  /** Whether positive delta is good (default: true for quality, false for flags) */
  positiveIsGood?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Whether to show as a badge with background */
  asBadge?: boolean;
}

export const DeltaBadge: React.FC<DeltaBadgeProps> = ({
  delta,
  label,
  positiveIsGood = true,
  size = 'small',
  asBadge = false,
}) => {
  if (delta === null || delta === undefined) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.disabled',
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        }}
      >
        —
      </Typography>
    );
  }

  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isNeutral = delta === 0;

  // Determine color based on direction and whether positive is good
  let color: string;
  if (isNeutral) {
    color = TREND_COLORS.stable;
  } else if ((isPositive && positiveIsGood) || (isNegative && !positiveIsGood)) {
    color = TREND_COLORS.improving;
  } else {
    color = TREND_COLORS.declining;
  }

  const iconSize = size === 'small' ? '0.875rem' : '1rem';
  const prefix = isPositive ? '+' : '';
  const displayValue = `${prefix}${delta}`;

  const Icon = isPositive ? PositiveIcon : isNegative ? NegativeIcon : NoChangeIcon;

  const content = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        color,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        fontWeight: 600,
        ...(asBadge && {
          bgcolor: `${color}15`,
          borderRadius: 1,
          padding: size === 'small' ? '2px 6px' : '4px 8px',
        }),
      }}
    >
      <Icon sx={{ fontSize: iconSize }} />
      <Typography variant="inherit" component="span">
        {displayValue}
        {label && ` ${label}`}
      </Typography>
    </Box>
  );

  return content;
};

export default DeltaBadge;

